/**
 * Cliente HTTP minimal. Inyecta Authorization si hay token activo y
 * traduce errores HTTP a un objeto tipado.
 *
 * No usamos axios para mantener el bundle del PWA pequeño — fetch del
 * navegador alcanza para todo lo del Sprint 1.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly details?: unknown,
  ) {
    super(`${status} ${code}`);
    this.name = 'ApiError';
  }
}

let bearerToken: string | null = null;

export function setAuthToken(token: string | null): void {
  bearerToken = token;
}

interface ApiRequestInit {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Default true: si llega un 401 con token_revoked, dispara `onAuthError`. */
  signalAuthErrors?: boolean;
}

type AuthErrorHandler = (error: ApiError) => void;
let onAuthError: AuthErrorHandler = () => undefined;

export function setOnAuthError(handler: AuthErrorHandler): void {
  onAuthError = handler;
}

/**
 * Variante de apiRequest que devuelve la Response cruda sin parsear.
 * Necesario para descargas autenticadas donde el cuerpo es binario (PDF/DOCX).
 * Inyecta el mismo Authorization Bearer que apiRequest.
 */
export async function apiFetchRaw(path: string): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: '*/*',
  };
  if (bearerToken) {
    headers.Authorization = `Bearer ${bearerToken}`;
  }
  const res = await fetch(path, {
    method: 'GET',
    headers,
    credentials: 'omit',
  });
  return res;
}

export async function apiRequest<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (init.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (bearerToken) {
    headers.Authorization = `Bearer ${bearerToken}`;
  }

  const requestInit: RequestInit = {
    method: init.method ?? 'GET',
    headers,
    credentials: 'omit',
  };
  if (init.body !== undefined) {
    requestInit.body = JSON.stringify(init.body);
  }
  const res = await fetch(path, requestInit);

  if (res.status === 204) {
    return undefined as T;
  }

  let payload: unknown = null;
  if (res.headers.get('content-type')?.includes('application/json')) {
    payload = await res.json().catch(() => null);
  }

  if (!res.ok) {
    const code =
      (payload && typeof payload === 'object' && 'error' in payload ? String(payload.error) : '') ||
      res.statusText;
    const error = new ApiError(res.status, code, payload);
    if (
      init.signalAuthErrors !== false &&
      res.status === 401 &&
      (code === 'token_revoked' || code === 'invalid_token' || code === 'missing_token')
    ) {
      onAuthError(error);
    }
    throw error;
  }

  return payload as T;
}
